import type { AppLocale, DeepPartialMessageTree } from "@airange/shared/i18n-common";

export const microLabPageMessages: Record<AppLocale, DeepPartialMessageTree> = {
  "zh-CN": {
    microLab: {
      pages: {
        login: {
          summary: "通过 AIRange SSO 登录后进入微实验教员工作台。",
          bootstrapTitle: "正在校验登录态",
          bootstrapDescription: "将自动识别当前 SSO 会话并建立微实验本地会话。",
          noSessionTitle: "还没有可用的登录态",
          noSessionDescription: "请先跳转到统一登录入口，完成 AIRange SSO 登录。",
          noAccessTitle: "当前账号没有微实验后台权限",
          noAccessDescription: "需要具备 teacher 或 platform_manager 角色后才能进入后台。",
          enterAction: "进入后台",
          switchIdentityAction: "切换账号登录",
          redetectAction: "重新识别权限",
        },
        oidcCallback: {
          completing: "正在完成 OIDC 登录...",
          missingCodeOrState: "OIDC 回调缺少 code/state 参数",
          bootstrapSessionFailed: "微实验后台本地会话初始化失败",
          callbackFailed: "OIDC 回调失败",
        },
        notFound: {
          title: "页面不存在",
          description: "当前地址没有对应的微实验后台页面。",
          backHome: "回到首页",
        },
        members: {
          title: "成员管理",
          subtitle: "管理真实成员清单、角色准入与统一身份选人。",
          loadFailed: "加载成员失败",
          searchDisplayName: "搜索显示名称",
          searchUsername: "搜索用户名",
          searchOrganization: "搜索组织",
          missingSsoUser: "SSO 中不存在该用户",
          emptyOrganization: "-",
          noApplicationRoles: "未授予应用级角色",
          editRolesAction: "调整角色",
          removeMemberAction: "移除成员",
          removeConfirmTitle: "确认移除 {name} 吗？",
          removeConfirmContent:
            "移除后该成员将不能进入微实验后台，也不能再被实验发布选为目标成员。",
          removeConfirmAction: "确认移除",
          removeSuccess: "成员已移除",
          governanceNotice:
            "这里是微实验的本地成员准入清单，显示名称和用户名以 SSO 当前资料为准；如果 SSO 中已不存在，会直接标出。未加入本地成员范围的用户，不应进入微实验后台，也不能成为定向发布对象。",
          addSubmitAction: "加入成员",
          addSuccess: "已新增 {count} 名成员",
          selectionHint: "先从统一身份组织树中多选成员，回到当前抽屉预览后再确认加入本地成员清单。",
          selectedTitle: "已选成员",
          selectedEmpty: "尚未选择成员",
          pickerTitle: "从统一身份中心添加成员",
          pickerSubmitAction: "带回已选成员",
          organizationTreeTitle: "组织树",
          pendingMembersTitlePrefix: "待加入成员",
          roleDrawerTitle: "调整应用角色",
          roleDrawerTitleWithName: "调整应用角色 · {name}",
          roleUpdateSuccess: "成员角色已更新",
          roleCatalogLoading: "角色目录仍在加载，请稍后再试。",
          roleCatalogUnavailable: "角色目录加载失败或合同无效，不能调整成员角色。",
          roleHint:
            "不勾选任何应用级角色时，该成员只保留本地成员记录，不能执行成员管理、共享库审核等后台操作。",
        },
        schemes: {
          title: "实验方案",
          subtitle: "在统一工作台里维护教员自有方案与共享复用主链。",
          privateSubtitle: "管理我创建的方案、草稿版本和来源关系，并从这里进入版本维护。",
          publicSubtitle: "浏览已通过共享审核的方案版本，查看详情后直接引用或克隆到我的方案。",
          scopeSubtitles: {
            mine: "管理我创建的方案、草稿版本和来源关系，并从这里进入版本维护。",
            shared_with_me: "查看其他教员直接共享给当前账号的方案。",
            public: "浏览已通过共享审核的方案版本，查看详情后直接引用或克隆到我的方案。",
            all: "管理员全量治理视图，用于跨 owner 检查全部实验方案与共享治理状态。",
          },
        },
        library: {
          title: "共享方案库",
          subtitle: "浏览已审核通过的共享版本，并将其引用或克隆回自己的方案工作区。",
        },
        schemeVersions: {
          title: "方案版本管理",
          subtitle:
            "在独立工作面维护版本、环境资产与结构化步骤，避免把多个版本挤进同一个方案抽屉。",
          currentSchemeContext: "{subtitle} 当前方案：{name}（{code}）",
        },
        schemeReviews: {
          title: "共享审核",
          subtitle: "管理员处理共享送审队列，并维护驳回、已通过和已下线记录。",
        },
        releases: {
          title: "实验发布",
          subtitle: "创建发布草稿、选择目标成员，并推进发布与关闭状态。",
        },
        releaseDetail: {
          title: "发布运营",
          subtitle: "围绕单条发布查看摘要、补充目标成员，并直接处理已经形成的实验实例。",
          currentReleaseContext: "{subtitle} 当前发布：{name}（{code}）",
          invalidReleaseContext: "当前地址里的发布 ID 无效，无法加载发布详情。",
          snapshot: {
            overviewTitle: "版本快照摘要",
            environmentEmpty: "未绑定执行环境",
            tagsEmpty: "当前快照没有实验标签。",
            durationEmpty: "待设置",
            durationMinutes: "约 {minutes} 分钟",
            durationHours: "约 {hours} 小时",
            durationHoursMinutes: "约 {hours} 小时 {minutes} 分钟",
            objectiveEmpty: "当前发布快照没有实验目标。",
            manualVisibleHint: "学员进入当前实验后可直接查看正式手册。",
            manualHiddenHint: "学员端不展示正式手册，管理员仍可在这里核对发布快照内容。",
            manualEmpty: "当前发布快照没有 Markdown 手册。",
            ksaEmpty: "当前发布快照没有 KSA 绑定。",
            stepInstructionEmpty: "当前步骤没有操作说明。",
            stepsEmpty: "当前发布快照没有结构化步骤。",
          },
        },
        releaseCreate: {
          title: "新建实验发布",
          subtitle: "按步骤确认来源方案版本、发布设置、目标成员和快照摘要，再正式生成发布草稿。",
        },
        runs: {
          title: "实例审计",
          subtitle: "跨发布检索已经形成的学生实验实例，聚焦运行状态、判定结果与排障留痕。",
          empty: "当前没有实验实例。",
        },
      },
    },
  },
  en: {
    microLab: {
      pages: {
        schemes: {
          title: "Schemes",
          subtitle: "Use one workspace for personal schemes and shared-scheme reuse.",
          privateSubtitle:
            "Manage the schemes owned by the current instructor, then open the dedicated version workspace from here.",
          publicSubtitle:
            "Browse approved shared schemes, inspect the details, and reference or clone them back into your own workspace.",
          scopeSubtitles: {
            mine: "Manage the schemes owned by the current instructor, then open the dedicated version workspace from here.",
            shared_with_me:
              "Inspect schemes directly shared with the current account by other instructors.",
            public:
              "Browse approved shared schemes, inspect the details, and reference or clone them back into your own workspace.",
            all: "Admin governance view for inspecting all schemes across owners and their sharing state.",
          },
        },
        schemeReviews: {
          title: "Shared Reviews",
          subtitle:
            "Admins process the shared-review queue and maintain pending, approved, rejected, and offline records.",
        },
        library: {
          title: "Shared Scheme Library",
          subtitle:
            "Browse approved shared versions and reference or clone them back into your own scheme workspace.",
        },
        oidcCallback: {
          completing: "Completing OIDC sign-in...",
          missingCodeOrState: "The OIDC callback is missing the code/state parameters",
          bootstrapSessionFailed: "Failed to initialize the local Micro Lab admin session",
          callbackFailed: "OIDC callback failed",
        },
        members: {
          title: "Members",
          subtitle: "Manage the real member allowlist, role access, and SSO member selection.",
          loadFailed: "Failed to load members",
          searchDisplayName: "Search display name",
          searchUsername: "Search username",
          searchOrganization: "Search organization",
          missingSsoUser: "This user no longer exists in SSO",
          emptyOrganization: "-",
          noApplicationRoles: "No application roles granted",
          editRolesAction: "Adjust Roles",
          removeMemberAction: "Remove Member",
          removeConfirmTitle: "Remove {name}?",
          removeConfirmContent:
            "After removal, this member can no longer access the Micro Lab admin or be selected as a targeted release member.",
          removeConfirmAction: "Confirm Remove",
          removeSuccess: "Member removed",
          governanceNotice:
            "This is the local Micro Lab member allowlist. Display names and usernames follow the current SSO profile, and missing SSO users are marked directly. Users outside the local member scope should not access the Micro Lab admin or become targeted release members.",
          addSubmitAction: "Add Members",
          addSuccess: "Added {count} members",
          selectionHint:
            "Select multiple users from the SSO organization tree first, then return to this drawer to preview and confirm the local member additions.",
          selectedTitle: "Selected Members",
          selectedEmpty: "No members selected",
          pickerTitle: "Add Members from Identity Center",
          pickerSubmitAction: "Use Selected Members",
          organizationTreeTitle: "Organization Tree",
          pendingMembersTitlePrefix: "Pending Members",
          roleDrawerTitle: "Adjust Application Roles",
          roleDrawerTitleWithName: "Adjust Application Roles · {name}",
          roleUpdateSuccess: "Member roles updated",
          roleCatalogLoading: "The role catalog is still loading. Try again shortly.",
          roleCatalogUnavailable:
            "The role catalog failed to load or violates the contract, so member roles cannot be adjusted.",
          roleHint:
            "If no application roles are selected, the member only keeps the local member record and cannot perform member management or shared-library review actions.",
        },
        releaseDetail: {
          title: "Release Operations",
          subtitle:
            "Review one release, add target members, and operate on the run instances already formed from it.",
          currentReleaseContext: "{subtitle} Current release: {name} ({code})",
          invalidReleaseContext:
            "The release ID in the current URL is invalid, so the release detail page cannot be loaded.",
          snapshot: {
            overviewTitle: "Snapshot Overview",
            environmentEmpty: "No environment bound",
            tagsEmpty: "No experiment tags in this snapshot.",
            durationEmpty: "To be set",
            durationMinutes: "About {minutes} minutes",
            durationHours: "About {hours} hours",
            durationHoursMinutes: "About {hours} hours {minutes} minutes",
            objectiveEmpty: "No lab objective in this release snapshot.",
            manualVisibleHint: "Learners can open the formal manual directly in the current lab.",
            manualHiddenHint:
              "The learner side hides the formal manual, but admins can still review the released content here.",
            manualEmpty: "No Markdown manual in this release snapshot.",
            ksaEmpty: "No KSA bindings in this release snapshot.",
            stepInstructionEmpty: "No instruction for this step.",
            stepsEmpty: "No structured steps in this release snapshot.",
          },
        },
        runs: {
          title: "Run Audit",
          subtitle:
            "Search already-formed learner lab instances across releases and focus on status, results, and troubleshooting audit trails.",
          empty: "No run instances yet.",
        },
        schemeVersions: {
          currentSchemeContext: "{subtitle} Current scheme: {name} ({code})",
        },
      },
    },
  },
  ja: {
    microLab: {
      pages: {
        library: {
          title: "共有プランライブラリ",
          subtitle:
            "承認済みの共有バージョンを確認し、自分のプラン作業領域へ参照または複製できます。",
        },
      },
    },
  },
};
